import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import BlogDetail from './BlogDetail';

const BlogSection = styled.section`
  background-color: var(--light-bg);
  padding: 100px 0;
`;

const BlogContainer = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
`;

const SectionSubtitle = styled(motion.span)`
  display: inline-block;
  font-size: 1rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 12px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: 30px;
    background-color: var(--secondary-color);
    transform: translateY(-50%);
  }
  
  &:before {
    right: calc(100% + 10px);
  }
  
  &:after {
    left: calc(100% + 10px);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.1rem;
  color: var(--text-muted);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    
    .blog-image img {
      transform: scale(1.05);
    }
  }
`;

const BlogImageContainer = styled.div`
  position: relative;
  height: 220px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
`;

const BlogCategory = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: var(--primary-dark);
  color: white;
  padding: 8px 15px;
  border-radius: 30px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 1;
`;

const BlogContent = styled.div`
  padding: 25px;
`;

const BlogDate = styled.div`
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 6px;
  }
`;

const BlogTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 15px;
  line-height: 1.4;
`;

const BlogExcerpt = styled.p`
  font-size: 0.95rem;
  color: var(--text-muted);
  line-height: 1.7;
  margin-bottom: 20px;
`;

const BlogFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid var(--grey-200);
`;

const BlogAuthor = styled.div`
  display: flex;
  align-items: center;
  
  img {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 10px;
  }
  
  span {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-dark);
  }
`;

const ReadMoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 6px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: var(--secondary-color);
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const ViewAllButton = styled.button`
  margin-top: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--primary-color);
  font-weight: 600;
  padding: 14px 30px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  background-color: white;
  border: 2px solid var(--primary-color);
  
  svg {
    margin-left: 8px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Blog = () => {
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Sample blog posts - replace with your actual content
  const blogPosts = [
    {
      id: 1,
      title: "The Future of Cloud Computing in Enterprise Environments",
      excerpt: "Explore how cloud computing is transforming enterprise IT infrastructure and enabling digital transformation across industries.",
      date: "April 24, 2025",
      category: "Cloud",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      author: {
        name: "Michael Johnson",
        avatar: "https://randomuser.me/api/portraits/men/32.jpg"
      }
    },
    {
      id: 2,
      title: "5 Essential Cybersecurity Measures Every Business Should Implement",
      excerpt: "Protect your organization's sensitive data with these fundamental cybersecurity practices that can prevent costly breaches.",
      date: "April 18, 2025",
      category: "Security",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      author: {
        name: "Sarah Williams",
        avatar: "https://randomuser.me/api/portraits/women/45.jpg"
      }
    },
    {
      id: 3,
      title: "How AI is Revolutionizing Software Development Processes",
      excerpt: "Artificial intelligence is changing how we build software, from automated testing to intelligent code suggestions and optimization.",
      date: "April 10, 2025",
      category: "AI",
      image: "https://images.unsplash.com/photo-1520085601670-ee14aa5fa3e8?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      author: {
        name: "David Chen",
        avatar: "https://randomuser.me/api/portraits/men/67.jpg"
      }
    },
    {
      id: 4,
      title: "The Impact of Progressive Web Apps on User Engagement",
      excerpt: "Discover how PWAs are changing the mobile web landscape by offering app-like experiences without the need for installation.",
      date: "April 5, 2025",
      category: "Web Dev",
      image: "https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      author: {
        name: "Priya Sharma",
        avatar: "https://randomuser.me/api/portraits/women/32.jpg"
      }
    },
    {
      id: 5,
      title: "Implementing DevOps: Challenges and Best Practices",
      excerpt: "Learn about the common hurdles organizations face when adopting DevOps and strategies to overcome them successfully.",
      date: "March 28, 2025",
      category: "DevOps",
      image: "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      author: {
        name: "James Wilson",
        avatar: "https://randomuser.me/api/portraits/men/22.jpg"
      }
    },
    {
      id: 6,
      title: "Blockchain Beyond Cryptocurrency: Enterprise Applications",
      excerpt: "Explore how blockchain technology is being used in supply chain, healthcare, and other industries beyond its financial origins.",
      date: "March 20, 2025",
      category: "Blockchain",
      image: "https://images.unsplash.com/photo-1639762681057-408e52192e55?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      author: {
        name: "Alex Thompson",
        avatar: "https://randomuser.me/api/portraits/men/42.jpg"
      }
    }
  ];

  const handlePostClick = (post) => {
    setSelectedPost(post);
    // Scroll to top of the blog section
    document.getElementById('blog').scrollIntoView();
  };
  
  const handleBackToBlog = () => {
    setSelectedPost(null);
  };
  
  return (
    <BlogSection id="blog">
      {selectedPost ? (
        <BlogDetail post={selectedPost} onBack={handleBackToBlog} />
      ) : (
      <BlogContainer>
        <SectionHeader
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <SectionSubtitle variants={fadeInUp}>Our Blog</SectionSubtitle>
          <SectionTitle variants={fadeInUp}>
            Latest <span>Insights</span> & News
          </SectionTitle>
          <SectionDescription variants={fadeInUp}>
            Stay up-to-date with the latest trends, technologies, and best practices in the world of IT and software development through our regularly updated blog.
          </SectionDescription>
        </SectionHeader>
        
        <BlogGrid>
          {(showAllPosts ? blogPosts : blogPosts.slice(0, 3)).map((post, index) => (
            <BlogCard
              key={post.id}
              as={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.5, delay: index * 0.1 }
                }
              }}
              onClick={() => handlePostClick(post)}
              style={{ cursor: 'pointer' }}
            >
              <BlogImageContainer className="blog-image">
                <img src={post.image} alt={post.title} />
                <BlogCategory>{post.category}</BlogCategory>
              </BlogImageContainer>
              <BlogContent>
                <BlogDate>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                  </svg>
                  {post.date}
                </BlogDate>
                <BlogTitle>{post.title}</BlogTitle>
                <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                <BlogFooter>
                  <BlogAuthor>
                    <img src={post.author.avatar} alt={post.author.name} />
                    <span>{post.author.name}</span>
                  </BlogAuthor>
                  <ReadMoreLink onClick={() => handlePostClick(post)}>
                    Read More
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z"/>
                    </svg>
                  </ReadMoreLink>
                </BlogFooter>
              </BlogContent>
            </BlogCard>
          ))}
        </BlogGrid>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <ViewAllButton onClick={() => setShowAllPosts(!showAllPosts)}>
            {showAllPosts ? 'Show Less' : 'View All Articles'}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z"/>
            </svg>
          </ViewAllButton>
        </motion.div>
      </BlogContainer>
      )}
    </BlogSection>
  );
};

export default Blog;
